import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { useState } from "react";

import { ModalOverlay } from "@/components/ModalOverlay";
import { apiClient } from "@/lib/api";
import { describe } from "@/lib/api-error";
import { cn } from "@/lib/utils";
import type {
  ConfigurationSetting,
  ConfigurationSettingCreateRequest,
  ConfigurationSettingPatchRequest,
  ConfigurationSettingScope,
  ConfigurationSettingValueType,
} from "@tilt/admin-sdk";

/**
 * Configuration Settings Catalog tab (#168 PR 3).
 *
 * Super_admin curates the rows in ``configuration_setting_catalog_tbl``.
 * Each row defines one well-known key in ``*_tbl.config`` JSONB
 * with a ``value_type`` (drives input control downstream) and a
 * ``value_schema`` JSONB (drives validation). The org/loc/partner
 * Configuration tab will read this catalog in PR 4 and render
 * type-aware input controls instead of free-form chip strings.
 *
 * For now this tab is the curate surface only - the consumer side
 * still uses the existing JsonConfigEditor, which we'll swap in
 * the next PR.
 */

const VALUE_TYPES: ConfigurationSettingValueType[] = [
  "email",
  "phone",
  "url",
  "text_short",
  "text_long",
  "hex_color",
  "timezone",
  "tipping_rule",
  "integer",
  "boolean",
  "select",
];

const ALL_SCOPES: ConfigurationSettingScope[] = ["partner", "organization", "location"];

export function ConfigurationSettingsTab(): JSX.Element {
  const qc = useQueryClient();
  const [showInactive, setShowInactive] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<ConfigurationSetting | null>(null);

  const query = useQuery({
    queryKey: ["configuration-settings"],
    queryFn: () => apiClient.listConfigurationSettings(),
    staleTime: 30_000,
  });

  const invalidate = () => void qc.invalidateQueries({ queryKey: ["configuration-settings"] });

  if (query.isError) {
    return (
      <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
        {describe(query.error)}
      </div>
    );
  }

  const settings = query.data?.settings ?? [];
  const visible = showInactive ? settings : settings.filter((s) => s.is_active);

  return (
    <div className="space-y-6">
      <section className="space-y-4">
        <header className="flex flex-wrap items-end justify-between gap-3">
          <div className="max-w-2xl">
            <h2 className="text-sm font-semibold">Configuration settings</h2>
            <p className="mt-1 text-xs text-muted-foreground">
              Catalog of well-defined keys partner / org / location admins can set on their
              Configuration tab. Each row defines its value type and validation schema; the consumer
              UI renders the right input control per type.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <label className="flex items-center gap-1 text-xs text-muted-foreground">
              <input
                type="checkbox"
                checked={showInactive}
                onChange={(e) => setShowInactive(e.target.checked)}
              />
              Show retired
            </label>
            <button
              type="button"
              onClick={() => setCreateOpen(true)}
              className="inline-flex items-center gap-1 rounded-md bg-primary px-2 py-1 text-xs font-medium text-primary-foreground hover:opacity-90"
            >
              <Plus className="h-3 w-3" /> New setting
            </button>
          </div>
        </header>

        {query.isLoading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : visible.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No {showInactive ? "" : "active "}settings yet. Add one with “New setting”.
          </p>
        ) : (
          <ul className="divide-y rounded-md border">
            {visible.map((s) => (
              <SettingRow
                key={s.id}
                setting={s}
                onEdit={() => setEditing(s)}
                onMutated={invalidate}
              />
            ))}
          </ul>
        )}

        {createOpen && (
          <SettingDialog
            mode="create"
            onClose={() => setCreateOpen(false)}
            onMutated={() => {
              invalidate();
              setCreateOpen(false);
            }}
          />
        )}
        {editing && (
          <SettingDialog
            mode="edit"
            setting={editing}
            onClose={() => setEditing(null)}
            onMutated={() => {
              invalidate();
              setEditing(null);
            }}
          />
        )}
      </section>
    </div>
  );
}

function SettingRow({
  setting,
  onEdit,
  onMutated,
}: {
  readonly setting: ConfigurationSetting;
  readonly onEdit: () => void;
  readonly onMutated: () => void;
}): JSX.Element {
  const toggle = useMutation({
    mutationFn: () =>
      apiClient.patchConfigurationSetting(setting.id, { is_active: !setting.is_active }),
    onSuccess: onMutated,
  });
  const del = useMutation({
    mutationFn: () => apiClient.deleteConfigurationSetting(setting.id),
    onSuccess: onMutated,
  });

  return (
    <li className="flex flex-col gap-1 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
      <div className="min-w-0 space-y-0.5">
        <div className="flex items-center gap-2">
          <span className="font-medium">{setting.display_name}</span>
          <span className="rounded bg-muted px-1.5 py-0.5 font-mono text-[10px] text-muted-foreground">
            {setting.slug}
          </span>
          <span className="rounded bg-indigo-100 px-1.5 py-0.5 text-[10px] font-medium text-indigo-800">
            {setting.value_type}
          </span>
          {!setting.is_active && (
            <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-800">
              retired
            </span>
          )}
        </div>
        {setting.description && (
          <p className="line-clamp-2 text-xs text-muted-foreground">{setting.description}</p>
        )}
        <p className="text-[10px] uppercase tracking-wide text-muted-foreground">
          scopes:{" "}
          {Array.isArray(setting.applicable_scopes) ? setting.applicable_scopes.join(" / ") : "-"} ·
          cascade: {setting.cascade_strategy}
          {setting.consumer_app ? ` · consumer: ${setting.consumer_app}` : ""}
        </p>
      </div>
      <div className="flex shrink-0 items-center gap-1.5">
        <button
          type="button"
          onClick={() => toggle.mutate()}
          disabled={toggle.isPending}
          className="rounded-md border bg-background px-2 py-1 text-xs hover:bg-muted"
        >
          {setting.is_active ? "Retire" : "Activate"}
        </button>
        <button
          type="button"
          onClick={onEdit}
          aria-label="Edit"
          className="rounded-md border bg-background px-2 py-1 text-xs hover:bg-muted"
        >
          <Pencil className="h-3 w-3" />
        </button>
        <button
          type="button"
          onClick={() => {
            if (confirm(`Delete setting “${setting.display_name}”?`)) del.mutate();
          }}
          disabled={del.isPending}
          aria-label="Delete"
          className="rounded-md border border-destructive/30 bg-destructive/5 px-2 py-1 text-xs text-destructive hover:bg-destructive/10"
        >
          <Trash2 className="h-3 w-3" />
        </button>
      </div>
    </li>
  );
}

function SettingDialog(
  props:
    | { readonly mode: "create"; readonly onClose: () => void; readonly onMutated: () => void }
    | {
        readonly mode: "edit";
        readonly setting: ConfigurationSetting;
        readonly onClose: () => void;
        readonly onMutated: () => void;
      },
): JSX.Element {
  const initial = props.mode === "edit" ? props.setting : null;
  const [slug, setSlug] = useState(initial?.slug ?? "");
  const [displayName, setDisplayName] = useState(initial?.display_name ?? "");
  const [description, setDescription] = useState(initial?.description ?? "");
  const [valueType, setValueType] = useState<ConfigurationSettingValueType>(
    initial?.value_type ?? "text_short",
  );
  const [valueSchema, setValueSchema] = useState<string>(
    JSON.stringify(initial?.value_schema ?? {}, null, 2),
  );
  const [scopes, setScopes] = useState<ConfigurationSettingScope[]>(
    // Belt-and-suspenders: the API has historically returned Data API
    // ``arrayValue`` envelopes here (fix landed 2026-04-27). Verify
    // it's actually an array before spreading so a stale wire shape
    // can't crash the form again.
    Array.isArray(initial?.applicable_scopes)
      ? [...(initial?.applicable_scopes as ConfigurationSettingScope[])]
      : ["partner", "organization", "location"],
  );
  const [cascadeStrategy, setCascadeStrategy] = useState(
    initial?.cascade_strategy ?? "closest_wins",
  );
  const [consumerApp, setConsumerApp] = useState(initial?.consumer_app ?? "");
  const [sortOrder, setSortOrder] = useState<string>(String(initial?.sort_order ?? 100));
  const [error, setError] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: (body: ConfigurationSettingCreateRequest) =>
      apiClient.createConfigurationSetting(body),
    onSuccess: () => props.onMutated(),
    onError: (err) => setError(describe(err)),
  });
  const patch = useMutation({
    mutationFn: (body: ConfigurationSettingPatchRequest) =>
      apiClient.patchConfigurationSetting(initial!.id, body),
    onSuccess: () => props.onMutated(),
    onError: (err) => setError(describe(err)),
  });

  const submitting = create.isPending || patch.isPending;

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    let parsedSchema: Record<string, unknown> = {};
    try {
      parsedSchema = valueSchema.trim() ? JSON.parse(valueSchema) : {};
      if (
        typeof parsedSchema !== "object" ||
        Array.isArray(parsedSchema) ||
        parsedSchema === null
      ) {
        throw new Error("value_schema must be a JSON object");
      }
    } catch (err) {
      setError(`value_schema: ${describe(err)}`);
      return;
    }
    if (scopes.length === 0) {
      setError("Pick at least one applicable scope.");
      return;
    }
    const sortOrderInt = Number.parseInt(sortOrder, 10);
    if (!Number.isFinite(sortOrderInt)) {
      setError("Sort order must be a whole number.");
      return;
    }
    if (props.mode === "create") {
      create.mutate({
        slug: slug.trim(),
        display_name: displayName.trim(),
        description: description.trim() || null,
        value_type: valueType,
        value_schema: parsedSchema,
        applicable_scopes: scopes,
        cascade_strategy: cascadeStrategy,
        consumer_app: consumerApp.trim() || null,
        sort_order: sortOrderInt,
      });
    } else {
      patch.mutate({
        display_name: displayName.trim(),
        description: description.trim() || null,
        value_type: valueType,
        value_schema: parsedSchema,
        applicable_scopes: scopes,
        cascade_strategy: cascadeStrategy,
        consumer_app: consumerApp.trim() || null,
        sort_order: sortOrderInt,
      });
    }
  }

  return (
    <ModalOverlay
      onClose={props.onClose}
      ariaLabel={
        props.mode === "create" ? "New configuration setting" : "Edit configuration setting"
      }
    >
      <form
        onSubmit={onSubmit}
        className="w-full max-w-2xl space-y-4 rounded-lg bg-card p-5 shadow-xl"
      >
        <header>
          <h2 className="text-base font-semibold">
            {props.mode === "create" ? "New configuration setting" : "Edit setting"}
          </h2>
          <p className="text-xs text-muted-foreground">
            Defines one well-known key. Slug + value type are immutable on edit because historical
            config writes reference them.
          </p>
        </header>

        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Slug">
            <input
              type="text"
              required
              value={slug}
              onChange={(e) => setSlug(e.target.value)}
              disabled={props.mode === "edit"}
              className="w-full rounded-md border bg-background px-2 py-1.5 font-mono text-xs disabled:opacity-50"
              placeholder="receipt_header"
            />
          </Field>
          <Field label="Display name">
            <input
              type="text"
              required
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              className="w-full rounded-md border bg-background px-2 py-1.5 text-sm"
              placeholder="Receipt header"
            />
          </Field>
        </div>

        <Field label="Description">
          <textarea
            value={description ?? ""}
            onChange={(e) => setDescription(e.target.value)}
            rows={2}
            className="w-full rounded-md border bg-background px-2 py-1.5 text-sm"
            placeholder="Help text shown on the consumer-side Configuration tab."
          />
        </Field>

        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Value type">
            <select
              value={valueType}
              onChange={(e) => setValueType(e.target.value as ConfigurationSettingValueType)}
              disabled={props.mode === "edit"}
              className="w-full rounded-md border bg-background px-2 py-1.5 text-sm disabled:opacity-50"
            >
              {VALUE_TYPES.map((vt) => (
                <option key={vt} value={vt}>
                  {vt}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Cascade strategy">
            <select
              value={cascadeStrategy}
              onChange={(e) =>
                setCascadeStrategy(e.target.value as "closest_wins" | "partner_only" | "merge")
              }
              className="w-full rounded-md border bg-background px-2 py-1.5 text-sm"
            >
              <option value="closest_wins">closest_wins (default)</option>
              <option value="partner_only">partner_only</option>
              <option value="merge">merge</option>
            </select>
          </Field>
        </div>

        <Field label="Applicable scopes">
          <div className="flex gap-3">
            {ALL_SCOPES.map((s) => (
              <label key={s} className="flex items-center gap-1 text-xs">
                <input
                  type="checkbox"
                  checked={scopes.includes(s)}
                  onChange={(e) => {
                    setScopes((prev) =>
                      e.target.checked ? [...prev, s] : prev.filter((x) => x !== s),
                    );
                  }}
                />
                {s}
              </label>
            ))}
          </div>
        </Field>

        <Field
          label="Value schema (JSON)"
          hint='Type-specific constraints. e.g. {"max_length": 200} for text_short, {"options": ["a","b"]} for select.'
        >
          <textarea
            value={valueSchema}
            onChange={(e) => setValueSchema(e.target.value)}
            rows={4}
            className="w-full rounded-md border bg-background px-2 py-1.5 font-mono text-xs"
            placeholder="{}"
          />
        </Field>

        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Consumer app (doc only)">
            <input
              type="text"
              value={consumerApp ?? ""}
              onChange={(e) => setConsumerApp(e.target.value)}
              className="w-full rounded-md border bg-background px-2 py-1.5 text-sm"
              placeholder="tilt-pos"
            />
          </Field>
          <Field label="Sort order">
            <input
              type="number"
              value={sortOrder}
              onChange={(e) => setSortOrder(e.target.value)}
              className="w-full rounded-md border bg-background px-2 py-1.5 text-sm"
            />
          </Field>
        </div>

        {error && (
          <div className="rounded-md bg-destructive/10 p-2 text-xs text-destructive">{error}</div>
        )}

        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={props.onClose}
            disabled={submitting}
            className="rounded-md border bg-background px-3 py-1.5 text-sm hover:bg-muted disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={submitting}
            className={cn(
              "rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground",
              "hover:opacity-90 disabled:opacity-50",
            )}
          >
            {submitting ? "Saving…" : props.mode === "create" ? "Create" : "Save changes"}
          </button>
        </div>
      </form>
    </ModalOverlay>
  );
}

function Field({
  label,
  hint,
  children,
}: {
  readonly label: string;
  readonly hint?: string;
  readonly children: React.ReactNode;
}): JSX.Element {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-xs font-medium text-muted-foreground">{label}</span>
      {children}
      {hint && <span className="text-[10px] text-muted-foreground">{hint}</span>}
    </label>
  );
}
